import { useRef, useEffect } from "react";
import { toast } from "sonner";
import { Save, Share2, Upload } from "lucide-react";
import { updateSetting, uploadSiteImage } from "../../lib/api";
import { PLACEHOLDER_DOMAIN, placeholder, TOAST_NO_PERMISSION, TOAST_IMAGE_UPLOADED, TOAST_IMAGE_ERROR, TOAST_SAVE_ERROR, TOAST_UPDATED } from "../../lib/constants";

export default function AdminSharingEditor({ readOnly = false, ogSettings, setOgSettings, saving, activeLang }) {
  const initialRef = useRef(null);

  useEffect(() => {
    if (ogSettings.og_title !== undefined && !initialRef.current) {
      initialRef.current = { og_title: ogSettings.og_title, og_description: ogSettings.og_description, og_image: ogSettings.og_image, og_url: ogSettings.og_url };
    }
  }, [ogSettings.og_title, ogSettings.og_description, ogSettings.og_image, ogSettings.og_url]);

  async function persist() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    const initial = initialRef.current || {};
    const changed = Object.entries(ogSettings).filter(([k, v]) => initial[k] !== v);
    if (changed.length === 0) { toast.info("Nada alterado"); return; }
    try {
      await Promise.all(changed.map(([key, value]) => updateSetting(key, value)));
      initialRef.current = { ...ogSettings };
      toast.success(TOAST_UPDATED);
    } catch {
      toast.error(TOAST_SAVE_ERROR);
    }
  }

  return (
    <fieldset data-id="admin-sharing" disabled={readOnly} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="space-y-6 max-w-4xl">
        <div className="bg-white border border-fendi p-8 shadow-sm">
          <p className="eyebrow mb-2 flex items-center gap-2 text-champagne"><Share2 size={16} /> Pré-visualização em Redes Sociais</p>
          <p className="text-warmgray text-xs mb-8">Configure como o link aparece ao ser compartilhado.</p>

          <div className="bg-fendi/10 border border-fendi/20 rounded-lg p-6 mb-8 max-w-md">
            <div className="bg-white rounded-lg overflow-hidden shadow-sm">
              {ogSettings.og_image && (
                <img src={ogSettings.og_image} alt="Preview" className="w-full h-44 object-cover" />
              )}
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-warmgray">{new URL(ogSettings.og_url || `https://${PLACEHOLDER_DOMAIN}`).hostname}</p>
                <p className="text-sm font-medium text-softblack mt-1 truncate">{ogSettings.og_title || "Hub Profissional"}</p>
                <p className="text-xs text-warmgray mt-1 line-clamp-2">{ogSettings.og_description || ""}</p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <label className="block">
              <span className="eyebrow text-[10px] text-warmgray">Título</span>
              <input className="editorial-field mt-2 !text-sm" value={ogSettings.og_title}
                onChange={(e) => setOgSettings({ ...ogSettings, og_title: e.target.value })}
                placeholder={placeholder("short")} />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-warmgray">Descrição</span>
              <textarea className="editorial-field mt-2 !text-sm" rows={2} value={ogSettings.og_description}
                onChange={(e) => setOgSettings({ ...ogSettings, og_description: e.target.value })}
                placeholder={placeholder("medium")} />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-warmgray">URL do site</span>
              <input className="editorial-field mt-2 !text-sm" value={ogSettings.og_url}
                onChange={(e) => setOgSettings({ ...ogSettings, og_url: e.target.value })}
                placeholder={`https://${PLACEHOLDER_DOMAIN}`} />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-warmgray">Imagem (1200×630 recomendado)</span>
              <div className="flex gap-3 mt-2">
                <input className="editorial-field !text-sm flex-1" value={ogSettings.og_image}
                  onChange={(e) => setOgSettings({ ...ogSettings, og_image: e.target.value })}
                  placeholder={`https://${PLACEHOLDER_DOMAIN}`} />
                {!readOnly && (
                  <label className="btn-primary !py-2 !px-4 cursor-pointer flex items-center gap-2 shrink-0">
                    <Upload size={14} /> Upload
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const url = await uploadSiteImage(file);
                          setOgSettings({ ...ogSettings, og_image: url });
                          toast.success(TOAST_IMAGE_UPLOADED);
                        } catch {
                          toast.error(TOAST_IMAGE_ERROR);
                        }
                      }} />
                  </label>
                )}
              </div>
            </label>
          </div>

          {!readOnly && (
            <div className="flex justify-end mt-8 pt-4 border-t border-fendi/10">
              <button
                onClick={persist}
                disabled={saving}
                className="btn-primary !px-8 !py-2 !text-[10px]"
              >
                <Save size={12} className="mr-2" /> Guardar
              </button>
            </div>
          )}
        </div>
        </div>
      </fieldset>
  );
}
